import { createContext, useCallback, useContext, useState } from 'react';
import * as R from 'remeda';
import {
  useQueries,
  useQueryClient,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { v4 as uuidv4 } from 'uuid';
import { endpoints, getIDFromUrl, Url } from '@rossum/api-client';
import { AnnotationStatus } from '@rossum/api-client/shared';
import {
  AsyncTask,
  DocumentsDownloadTaskResult,
  documentsDownloadTaskResultSchema,
} from '@rossum/api-client/tasks';
import { uploadSchema } from '@rossum/api-client/uploads';
import { api } from '../../lib/apiClient';
import {
  attachmentStatusSchema,
  ParsedAttachmentStatus,
} from '../../types/attachmentStatus';
import { handleDownloadOriginalFilesResult } from './asyncTaskResultHandlers';
import { resolveDetailForDocument } from './helpers/resolveDetailForDocument';

type TaskMetadata = {
  fileName: string;
  queueName: string;
  queueUrl: Url;
  detail?: string;
};

type AsynchronousTask = {
  taskId: number;
  timestamp: number;
  metadata?: TaskMetadata;
};

export type AsyncTasks = (UseQueryResult<
  AsyncTask & { timestamp: number; metadata?: TaskMetadata }
> & { timestamp: number })[];

export type UnprocessedDocument = {
  taskId: string;
  timestamp: number;
  data: {
    type: 'uploads';
    status: ParsedAttachmentStatus;
    detail?: string;
    fileName?: TaskMetadata['fileName'];
    queueName?: TaskMetadata['queueName'];
  };
};
export type UnprocessedDocuments = UnprocessedDocument[];

export type ImportTask = {
  annotationUrl: string;
  annotationStatus?: AnnotationStatus;
  queueUrl?: string;
  queueName?: string;
  fileName?: string;
  timestamp: number;
};

type TaskContextValue = {
  tasks: {
    asyncTasks: AsyncTasks[number][];
    unprocessedDocuments: UnprocessedDocuments;
    importTasks: ImportTask[];
  };
  registerAsyncTask: (task: AsynchronousTask) => void;
  unregisterAsyncTask: (taskId: number) => void;
  registerUnprocessedDocument: (task: UnprocessedDocument) => void;
  clearAllTasks: () => void;
};

const TaskContext = createContext<TaskContextValue | undefined>(undefined);

export const TaskContextProvider = ({
  children,
  onUploadCreated,
}: {
  children?: React.ReactNode;
  onUploadCreated?: () => void;
}) => {
  const intl = useIntl();
  const [asyncTasks, setAsyncTasks] = useState<AsynchronousTask[]>([]);
  const [unprocessedDocuments, setUnprocessedDocuments] = useState<
    UnprocessedDocument[]
  >([]);
  const [importTasks, setImportTasks] = useState<ImportTask[]>([]);

  const queryClient = useQueryClient();

  const asyncTasksResults = useQueries<
    UseQueryOptions<
      AsyncTask & { timestamp: number; metadata?: TaskMetadata }
    >[]
  >({
    queries: asyncTasks.map(({ taskId, metadata, timestamp }) => ({
      queryKey: ['ASYNC_TASK', taskId] as const,
      queryFn: () =>
        api
          .request(endpoints.tasks.get(taskId, { noRedirect: true }))
          .then(response => ({ ...response, metadata, timestamp })),
      // refetch task info while status is running
      refetchInterval: (data, query) => {
        if (query.state.status === 'error' || data?.status !== 'running') {
          return 0;
        }

        return 1000 * 1;
      },
      onSuccess: data => {
        if (data.status === 'succeeded' && data.resultUrl) {
          // process the result of the task
          // call API with data.resultUrl, pass result to taskHandler
          if (data.type === 'documents_download') {
            api
              .request<DocumentsDownloadTaskResult, never, never>({
                endpoint: data.resultUrl,
                method: 'GET',
                responseSchema: documentsDownloadTaskResultSchema,
              })
              .then(resultResponse =>
                handleDownloadOriginalFilesResult(resultResponse, {})
              );
          } else if (data.type === 'upload_created') {
            api
              .request({
                endpoint: data.resultUrl,
                method: 'GET',
                responseSchema: uploadSchema,
              })
              .then(async ({ annotations, documents }) => {
                if (documents.length === annotations.length) {
                  setImportTasks(prev => [
                    ...prev,
                    ...annotations.map(url => ({
                      annotationUrl: url,
                      queueName: data.metadata?.queueName,
                      queueUrl: data.metadata?.queueUrl,
                      fileName: data.metadata?.fileName,
                      timestamp: data.timestamp,
                    })),
                  ]);
                } else {
                  const documentDetails = await api.unpaginatedRequest(
                    endpoints.documents.list
                  )({
                    id: documents.map(getIDFromUrl),
                  });

                  const [withAnnotations, withoutAnnotations] = R.partition(
                    documentDetails,
                    document => !!document.annotations.length
                  );

                  setUnprocessedDocuments(prev => [
                    ...prev,
                    ...withoutAnnotations.map((document, index) => {
                      const status = attachmentStatusSchema.parse(
                        document.attachmentStatus
                      );
                      return {
                        taskId: uuidv4(),
                        timestamp:
                          // we assume that on 0 index is the ZIP archive/original document
                          index === 0 ? timestamp : new Date().getTime(),
                        data: {
                          detail: resolveDetailForDocument(
                            document,
                            status,
                            {
                              documents,
                            },
                            intl
                          ),
                          type: 'uploads' as const,
                          status,
                          fileName: document.originalFileName ?? undefined,
                        },
                      };
                    }),
                  ]);

                  setImportTasks(prev => [
                    ...prev,
                    ...withAnnotations.flatMap(document => {
                      const annotationUrl = document.annotations[0];
                      if (!annotationUrl) {
                        return [];
                      }

                      return {
                        annotationUrl,
                        queueName: data.metadata?.queueName,
                        queueUrl: data.metadata?.queueUrl,
                        fileName: data.metadata?.fileName,
                        timestamp: new Date().getTime(),
                      };
                    }),
                  ]);
                }
                unregisterAsyncTask(data.id);
                onUploadCreated?.();
              });
          }
        }
      },
    })),
  });

  const registerAsyncTask = useCallback(
    ({ taskId, metadata, timestamp }: AsynchronousTask) => {
      setAsyncTasks(tasks => [...tasks, { taskId, metadata, timestamp }]);
    },
    []
  );

  const unregisterAsyncTask = useCallback((taskId: number) => {
    setAsyncTasks(tasks => tasks.filter(({ taskId: id }) => id !== taskId));
  }, []);

  const registerUnprocessedDocument = useCallback(
    (task: UnprocessedDocument) => {
      setUnprocessedDocuments(tasks => [...tasks, task]);
    },
    []
  );

  const clearAllTasks = useCallback(() => {
    setAsyncTasks([]);
    setUnprocessedDocuments([]);
    setImportTasks([]);
    queryClient.removeQueries({
      queryKey: ['ASYNC_TASK'],
    });
    queryClient.removeQueries({
      queryKey: ['UPLOADS_TASK'],
    });
  }, [queryClient]);

  return (
    <TaskContext.Provider
      value={{
        tasks: {
          asyncTasks: asyncTasksResults.map((r, index) => ({
            ...r,
            timestamp: asyncTasks[index]?.timestamp ?? Date.now(),
          })),
          unprocessedDocuments,
          importTasks,
        },
        registerAsyncTask,
        unregisterAsyncTask,
        registerUnprocessedDocument,
        clearAllTasks,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTaskContext = () => {
  const ctx = useContext(TaskContext);

  if (ctx === undefined) {
    throw new Error(
      '`useTaskContext` must be used within a TaskContext provider'
    );
  }

  return ctx;
};
